import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { ChevronDown, MapPin } from "lucide-react";
import React, { memo, useState, useRef, useEffect, useMemo } from "react";
import {
  ILocationMenuItem,
  ILocationMenuInsight,
} from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";

interface InsightSelectorProps {
  locations: ILocationMenuItem[];
  selectedInsightId?: number;
  onSelect: (insight: ILocationMenuInsight, locationId: number) => void;
  placeholder: string;
  searchPlaceholder: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  noResultsText: string;
  noInsightsInLocationText: string;
}

export const InsightSelector: React.FC<InsightSelectorProps> = memo(
  ({
    locations,
    selectedInsightId,
    onSelect,
    placeholder,
    searchPlaceholder,
    isLoading,
    isDisabled,
    noResultsText,
    noInsightsInLocationText,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Get selected insight name
    const selectedInsightName = useMemo(() => {
      if (!selectedInsightId) return "";
      for (const location of locations) {
        const insight = location.items.find((i) => i.id === selectedInsightId);
        if (insight) return insight.name;
      }
      return "";
    }, [selectedInsightId, locations]);

    // Filter locations and insights based on search
    const filteredLocations = useMemo(() => {
      if (!searchValue.trim()) return locations;

      const search = searchValue.toLowerCase();
      return locations
        .map((location) => {
          const locationMatches = location.name.toLowerCase().includes(search);
          const matchingInsights = location.items.filter((item) =>
            item.name.toLowerCase().includes(search)
          );

          if (locationMatches) {
            return location; // Show all insights if location matches
          } else if (matchingInsights.length > 0) {
            return { ...location, items: matchingInsights };
          }
          return null;
        })
        .filter((loc): loc is ILocationMenuItem => loc !== null);
    }, [locations, searchValue]);

    // Close on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setIsFocused(false);
          // Clear search when closing if there's a selected value
          if (selectedInsightName) {
            setSearchValue("");
          }
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedInsightName]);

    const handleInsightSelect = (
      insight: ILocationMenuInsight,
      locationId: number
    ) => {
      onSelect(insight, locationId);
      setIsOpen(false);
      setIsFocused(false);
      setSearchValue("");
    };

    const handleInputFocus = () => {
      if (!isDisabled) {
        setIsOpen(true);
        setIsFocused(true);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
      if (!isOpen) {
        setIsOpen(true);
      }
    };

    const hasInsights = locations.some((loc) => loc.items.length > 0);

    // Display value: show search value when focused/typing, otherwise show selected name
    const displayValue = isFocused ? searchValue : selectedInsightName || "";

    return (
      <Box ref={containerRef} position="relative" width="100%">
        {/* Input Field */}
        <InputGroup>
          <Input
            ref={inputRef}
            placeholder={isFocused ? searchPlaceholder : placeholder}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            isDisabled={isDisabled}
            h="44px"
            px={4}
            pr={10}
            bg="white"
            border="1px solid"
            borderColor={isOpen ? "#0884CC" : "#D5D7DA"}
            borderRadius="md"
            fontSize="16px"
            color="#414651"
            _placeholder={{ color: "#717680" }}
            _hover={{ borderColor: isDisabled ? "#D5D7DA" : "#A1A1AA" }}
            _focus={{
              borderColor: "#0884CC",
              boxShadow: "0 0 0 1px #0884CC",
            }}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed",
            }}
            transition="border-color 0.2s"
          />
          <InputRightElement h="44px" pr={2}>
            {isLoading ? (
              <Spinner size="sm" color="#0884CC" />
            ) : (
              <ChevronDown
                size={20}
                color="#71717A"
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            )}
          </InputRightElement>
        </InputGroup>

        {/* Dropdown */}
        {isOpen && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            border="1px solid"
            borderColor="#D5D7DA"
            borderRadius="md"
            boxShadow="lg"
            zIndex={1000}
            maxH="300px"
            overflow="hidden"
          >
            {/* Results List */}
            <List overflowY="auto" maxH="300px" spacing={0}>
              {!hasInsights ? (
                <ListItem p={4}>
                  <Text color="#717680" fontSize="14px" textAlign="center">
                    {noResultsText}
                  </Text>
                </ListItem>
              ) : filteredLocations.length === 0 ? (
                <ListItem p={4}>
                  <Text color="#717680" fontSize="14px" textAlign="center">
                    {noResultsText}
                  </Text>
                </ListItem>
              ) : (
                filteredLocations.map((location, index) => (
                  <Box key={location.id}>
                    {/* Location Header (non-clickable) */}
                    <Flex
                      px="10px"
                      py="4px"
                      pt={index === 0 ? "12px" : "4px"}
                      alignItems="center"
                      gap="6px"
                    >
                      <MapPin size={14} color="#0884CC" />
                      <Text fontSize="12px" fontWeight="500" color="#0884CC">
                        {location.name}
                      </Text>
                    </Flex>
                    {/* Insight Items */}
                    {location.items.length > 0 ? (
                      location.items.map((insight) => (
                        <ListItem
                          key={insight.id}
                          px={4}
                          py={2}
                          cursor="pointer"
                          bg={
                            selectedInsightId === insight.id
                              ? "#EBF8FF"
                              : "white"
                          }
                          _hover={{ bg: "#F0F9FF" }}
                          onClick={() =>
                            handleInsightSelect(insight, location.id)
                          }
                        >
                          <Text fontSize="14px" color="#414651" pl={2}>
                            {insight.name}
                          </Text>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem px={4} py={2}>
                        <Text
                          fontSize="12px"
                          color="#717680"
                          pl={2}
                          fontStyle="italic"
                        >
                          {noInsightsInLocationText}
                        </Text>
                      </ListItem>
                    )}
                  </Box>
                ))
              )}
            </List>
          </Box>
        )}
      </Box>
    );
  }
);
